/*
 * @author        zach.william@zumper.com (Zach William)
 * @copyright     Copyright (c) 2022, Zumper
 * @description   slicesEngrain.ts
 */

import { type PayloadAction, createSlice } from '@reduxjs/toolkit'
import type { EngrainMap } from '@zcode/bizos'

import { INITIAL_STATE, ROOT_NAME } from '../constants'
import type { EngrainValuesRoot, FullUnitObject } from '../types'

export const slicesEngrain = createSlice({
  name: ROOT_NAME,
  initialState: INITIAL_STATE,
  reducers: {
    setTempFiltersBedrooms: (
      state,
      action: PayloadAction<EngrainValuesRoot['tempFilters']['bedrooms']>
    ) => {
      state.tempFilters.bedrooms = action.payload
    },

    setTempFiltersPriceRange: (
      state,
      action: PayloadAction<EngrainValuesRoot['tempFilters']['priceRange']>
    ) => {
      state.tempFilters.priceRange = action.payload
    },

    toggleSelectedUnits: (state, action: PayloadAction<FullUnitObject>) => {
      if (action.payload) {
        const foundIndex = state.selectedUnits.findIndex(
          (e) => e?.engrainUnitId === action.payload?.engrainUnitId
        )

        if (foundIndex === -1) {
          state.selectedUnits = [action.payload]
        } else {
          state.selectedUnits.splice(foundIndex, 1)
        }
      }
    },
    clearSelectedUnits: (state, action: PayloadAction<null>) => {
      state.selectedUnits = []
    },
    setSelectedPageUnit: (state, action: PayloadAction<FullUnitObject>) => {
      state.selectedPageUnit = action.payload
    },

    setSelectedFloor: (state, action: PayloadAction<EngrainValuesRoot['selectedFloor']>) => {
      state.selectedFloor = action.payload
    },

    assignTempBedroomsToRootValue: (state) => {
      state.rootFilters.bedrooms = state.tempFilters.bedrooms
      // state.tempFilters = INITIAL_STATE.tempFilters
    },

    assignTempPriceToRootValue: (state) => {
      state.rootFilters.priceRange = state.tempFilters.priceRange
    },

    setPageKey: (state, action: PayloadAction<EngrainValuesRoot['pageKey']>) => {
      state.pageKey = action.payload
    },

    startFetchingEngrainMap: (
      state,
      { payload: { buildingId } }: PayloadAction<{ buildingId: number }>
    ) => {
      state.engrainMapMeta.fetching = true
      state.engrainMapMeta.fetchingKey = buildingId
    },

    receiveEngrainMap: (state, { payload }: PayloadAction<EngrainMap>) => {
      if (payload.assetId) {
        state.engrainMap = payload
      }
      state.engrainMapMeta.fetching = false
      state.engrainMapMeta.currentKey = state.engrainMapMeta.fetchingKey
      state.engrainMapMeta.fetchingKey = undefined
    },

    receiveFetchingEngrainMapError: (
      state,
      { payload: { buildingId, error } }: PayloadAction<{ buildingId: number; error: string }>
    ) => {
      state.engrainMapMeta.fetching = false
      state.engrainMapMeta.fetchingKey = undefined
      state.engrainMapMeta.currentKey = buildingId
      state.engrainMapMeta.error = error
    },
    receiveUnits: (state, action: PayloadAction<EngrainValuesRoot['units']>) => ({
      ...state,
      units: action.payload,
      unitsMeta: {
        fetching: false,
        currentKey: state.unitsMeta.fetchingKey,
        fetchingKey: undefined,
      },
    }),
    receiveFetchingUnitsError: (
      state,
      { payload: { buildingId, error } }: PayloadAction<{ buildingId: number; error: string }>
    ) => {
      state.unitsMeta.fetching = false
      state.unitsMeta.currentKey = buildingId
      state.unitsMeta.fetchingKey = undefined
      state.unitsMeta.error = error
    },
    startFetchingUnits: (
      state,
      { payload: { buildingId } }: PayloadAction<{ buildingId: number }>
    ) => {
      state.unitsMeta.fetching = true
      state.unitsMeta.fetchingKey = buildingId
    },
  },
})

export const { actions, reducer } = slicesEngrain
